export interface TermsContentType {
    title: string;
    general: TermsContentSectionShort;
    serviceProvision: TermsContentSection;
    paymentMethods: TermsContentSectionShort;
    refundPolicy: TermsContentSection;
    contact: TermsContentSection;
    amendments: TermsContentSectionShort;
}

export interface TermsContentSection {
    title: string;
    text: string[];
}

interface TermsContentSectionShort {
    title: string;
    text: string;
}

export const termsContent: Record<string, TermsContentType> = {
    en: {
        title: "Terms and Conditions",
        general: {
            title: "General Information",
            text: "JEMLOC® provides an AI-powered administration service for small businesses, helping automate and optimize daily operations, including client management, scheduling, payments, reminders, and analytics. By using Jemloc, you agree to the following terms.",
        },
        serviceProvision: {
            title: "Service Provision",
            text: [
                "Jemloc does not provide services directly but acts as an intelligent assistant to facilitate business operations.",
                "Businesses can use Jemloc to:",
                "- Create a website and apps for their brand.",
                "- Manage schedules and appointments.",
                "- Automate reminders and client notifications.",
                "- Process online payments securely.",
                "Once a booking, payment, or other transaction is processed, it is considered delivered.",
            ],
        },
        paymentMethods: {
            title: "Payment Methods",
            text: "We accept payments via Visa and MasterCard through WayForPay. All payments are processed securely, and users receive instant confirmation. Jemloc does not store any sensitive payment details.",
        },
        refundPolicy: {
            title: "Refund and Cancellation Policy",
            text: [
                "Jemloc facilitates payments but does not control individual refund policies. Each business defines its own refund and cancellation conditions.",
                "In case of disputes, Jemloc assists in coordinating resolutions between businesses and clients.",
                "If a refund is applicable, it will be processed back to the original payment method within 5–10 business days.",
                "If your business does not offer refunds or cancellations, this information must be clearly displayed in your settings.",
            ],
        },
        contact: {
            title: "Contact Information",
            text: [
                "Company Name: ФОП Георгіян Галина Юріївна",
                "Registration Code (ЄДРПОУ): 3086501566",
                "Legal Address: 61045, м. Харків, Отакара Яроша 12а, офіс 121",
                "Email: support@jemloc.com",
                "Phone: +380951241100",
            ],
        },
        amendments: {
            title: "Amendments",
            text: "Jemloc reserves the right to update these terms at any time. Continued use of the service after an update constitutes acceptance of the changes.",
        },
    },
    uk: {
        title: "Правила та умови",
        general: {
            title: "Загальна інформація",
            text: "JEMLOC® – це AI-адміністратор, який допомагає малому бізнесу автоматизувати управління, розклад, платежі та комунікацію з клієнтами. Використовуючи Jemloc, ви погоджуєтеся з наступними умовами.",
        },
        serviceProvision: {
            title: "Надання послуг",
            text: [
                "Jemloc не надає послуги безпосередньо, а виступає в ролі цифрового помічника.",
                "Платформа допомагає бізнесу:",
                "- Створювати сайти та додатки.",
                "- Керувати розкладом та записами клієнтів.",
                "- Автоматично надсилати нагадування про зустрічі та платежі.",
                "- Обробляти платежі онлайн.",
                "Після завершення запису, оплати або іншої операції вона вважається виконаною.",
            ],
        },
        paymentMethods: {
            title: "Способи оплати",
            text: "Jemloc підтримує оплату картками Visa та MasterCard через WayForPay. Усі платежі проходять безпечно, без збереження чутливих даних. Користувачі отримують підтвердження про успішну оплату.",
        },
        refundPolicy: {
            title: "Політика повернення коштів",
            text: [
                "Jemloc не контролює політику повернень кожного бізнесу – вона визначається індивідуально.",
                "У випадку суперечок ми допомагаємо координувати вирішення між бізнесом і клієнтом.",
                "Якщо повернення можливе, кошти будуть відшкодовані на початковий метод оплати протягом 5–10 робочих днів.",
                "Якщо ваш бізнес не передбачає повернень, ця інформація повинна бути чітко зазначена у ваших налаштуваннях.",
            ],
        },
        contact: {
            title: "Контактна інформація",
            text: [
                "Назва компанії: ФОП Георгіян Галина Юріївна",
                "Код ЄДРПОУ: 3086501566",
                "Юридична адреса: 61045, м. Харків, Отакара Яроша 12а, офіс 121",
                "Email: support@jemloc.com",
                "Телефон: +380951241100",
            ],
        },
        amendments: {
            title: "Зміни та оновлення",
            text: "Jemloc залишає за собою право змінювати ці умови в будь-який момент. Подальше використання сервісу після оновлення означає прийняття змін.",
        },
    },
    ru: {
        title: "Правила и условия",
        general: {
            title: "Общая информация",
            text: "JEMLOC® — это AI-администратор, который помогает малому бизнесу автоматизировать управление, расписание, платежи и взаимодействие с клиентами. Используя Jemloc, вы соглашаетесь с приведёнными ниже условиями.",
        },
        serviceProvision: {
            title: "Предоставление услуг",
            text: [
                "Jemloc не предоставляет услуги напрямую, а выступает в роли интеллектуального помощника для управления бизнесом.",
                "Платформа помогает предпринимателям:",
                "- Создавать сайт и приложения для их бренда.",
                "- Управлять расписанием и записями клиентов.",
                "- Автоматизировать напоминания и уведомления клиентов.",
                "- Безопасно обрабатывать онлайн-платежи.",
                "После завершения бронирования, оплаты или другой транзакции она считается исполненной.",
            ],
        },
        paymentMethods: {
            title: "Способы оплаты",
            text: "Jemloc поддерживает оплату картами Visa и MasterCard через WayForPay. Все платежи обрабатываются безопасно, без сохранения конфиденциальных данных. Клиенты получают подтверждение успешного платежа.",
        },
        refundPolicy: {
            title: "Политика возврата и отмены",
            text: [
                "Jemloc не контролирует индивидуальную политику возвратов бизнеса – каждый предприниматель устанавливает свои условия.",
                "В случае споров Jemloc помогает координировать решения между бизнесом и клиентами.",
                "Если возврат возможен, средства будут возвращены на исходный метод оплаты в течение 5–10 рабочих дней.",
                "Если ваш бизнес не предусматривает возвраты или отмены, эта информация должна быть четко указана в настройках.",
            ],
        },
        contact: {
            title: "Контактная информация",
            text: [
                "Название компании: ФОП Георгіян Галина Юріївна",
                "ИНН (ЄДРПОУ): 3086501566",
                "Юридический адрес: 61045, г. Харьков, Отакара Яроша 12а, офис 121",
                "Email: support@jemloc.com",
                "Телефон: +380951241100",
            ],
        },
        amendments: {
            title: "Изменения и обновления",
            text: "Jemloc оставляет за собой право вносить изменения в эти условия в любое время. Дальнейшее использование сервиса после обновления означает согласие с изменениями.",
        },
    },
};
